import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Camera, FileText, Upload, Video, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TutorSetupPage = () => {
  const navigate = useNavigate();
  const { user, setupTutor } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: user?.name || "",
    bio: "",
    photo_url: "",
    zoom_link: "",
    max_students: ""
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }

    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);

    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("access_token");
      const response = await axios.post(`${API}/upload/photo`, fd, {
        headers: { 
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });
      setFormData(prev => ({ ...prev, photo_url: response.data.url }));
      toast.success("Photo uploaded!");
    } catch (error) {
      toast.error("Failed to upload photo");
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.bio.trim() || !formData.photo_url || !formData.zoom_link.trim()) {
      toast.error("Please fill in all fields including photo and Zoom link");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        max_students: formData.max_students !== "" ? parseInt(formData.max_students, 10) : null,
      };
      await setupTutor(payload);
      toast.success("Profile setup complete!");
      navigate("/tutor/dashboard");
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12 px-6">
      <div className="max-w-lg mx-auto">
        <div className="brutalist-card p-8">
          <h1 className="text-3xl font-bold mb-2" data-testid="setup-title">Complete Your Profile</h1>
          <p className="text-[#4A4A4A] mb-8">
            Students and parents will see this information.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Photo Upload */}
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Profile Photo *
              </Label>
              
              <div className="mt-3 flex flex-col items-center gap-4">
                {previewUrl || formData.photo_url ? (
                  <img 
                    src={previewUrl || formData.photo_url} 
                    alt="Preview" 
                    className="w-32 h-32 rounded-full border-4 border-[#0A0A0A] object-cover"
                    data-testid="photo-preview"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full border-4 border-dashed border-[#0A0A0A]/30 flex items-center justify-center bg-[#F0EFEB]">
                    <Camera className="w-10 h-10 text-[#4A4A4A]" />
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="photo-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="brutalist-btn-secondary flex items-center gap-2 text-sm"
                  data-testid="upload-photo-btn"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4" /> {formData.photo_url ? "Change Photo" : "Upload Photo"}</>
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider">Your Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your full name"
                className="brutalist-input mt-2"
                required
                data-testid="tutor-name-input"
              />
            </div>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Bio *
              </Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell students about yourself, your teaching experience, and qualifications..."
                className="brutalist-input mt-2"
                rows={5}
                required
                data-testid="tutor-bio-input"
              />
              <p className="text-xs text-[#4A4A4A] mt-1">
                This will be displayed on course pages
              </p>
            </div>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <Video className="w-4 h-4" />
                Zoom Link *
              </Label>
              <Input
                value={formData.zoom_link}
                onChange={(e) => setFormData({ ...formData, zoom_link: e.target.value })}
                placeholder="https://zoom.us/j/your-meeting-id"
                className="brutalist-input mt-2"
                required
                data-testid="tutor-zoom-input"
              />
              <p className="text-xs text-[#4A4A4A] mt-1">
                Students will receive this link when they book a class with you
              </p>
            </div>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <Users className="w-4 h-4" />
                Student Capacity
              </Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={formData.max_students}
                onChange={(e) => setFormData({ ...formData, max_students: e.target.value })}
                placeholder="e.g. 5 (leave blank for no limit)"
                className="brutalist-input mt-2"
                data-testid="tutor-max-students-input"
              />
              <p className="text-xs text-[#4A4A4A] mt-1">
                Max students you can take at a time. Leave blank for unlimited.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !formData.photo_url || !formData.zoom_link.trim()}
              className="brutalist-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="complete-setup-btn"
            >
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Saving...</> : "Complete Setup"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TutorSetupPage;
